import { Button } from "@groot/ui/button";
import { Input } from "@groot/ui/input";
import { endOfMonthFormatted, startOfMonthFormatted } from "@/lib/date.utils";
import { cn } from "@/lib/utils";
import type { Account, Category, TransactionFilters as FilterType } from "@/types";
import dayjs from "dayjs";
import { ListFilter, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Button as AriaButton,
  Popover as AriaPopover,
  Dialog,
  DialogTrigger,
  Menu,
  MenuItem,
  MenuTrigger,
} from "react-aria-components";

interface TransactionFiltersProps {
  filters: FilterType;
  onFiltersChange: (filters: FilterType) => void;
  accounts: Account[];
  categories: Category[];
  /** Fields hidden from both the pill row and the add-filter menu (e.g.
   *  "account" on the account detail page, where the account is implied). */
  excludeFields?: FilterField[];
}

type FilterField = "date" | "category" | "account" | "amount" | "notes" | "reviewed";
type Operator = "is" | "isNot" | "contains" | "gt" | "gte" | "lt" | "lte";

interface Filter {
  field: FilterField;
  op: Operator;
  // biome-ignore lint/suspicious/noExplicitAny: complex filter values
  value: any;
}

const FIELD_LABELS: Record<FilterField, string> = {
  date: "Date",
  category: "Category",
  account: "Account",
  amount: "Amount",
  notes: "Notes",
  reviewed: "Review Status",
};

const OPERATORS: Record<FilterField, Array<{ op: Operator; label: string }>> = {
  date: [
    { op: "is", label: "is" },
    { op: "gt", label: "is after" },
    { op: "gte", label: "is after or on" },
    { op: "lt", label: "is before" },
    { op: "lte", label: "is before or on" },
  ],
  category: [
    { op: "is", label: "is" },
    { op: "isNot", label: "is not" },
  ],
  account: [
    { op: "is", label: "is" },
    { op: "isNot", label: "is not" },
  ],
  amount: [
    { op: "is", label: "is" },
    { op: "gt", label: "is greater than" },
    { op: "gte", label: "is greater than or equal" },
    { op: "lt", label: "is less than" },
    { op: "lte", label: "is less than or equal" },
  ],
  notes: [
    { op: "is", label: "is" },
    { op: "contains", label: "contains" },
  ],
  reviewed: [{ op: "is", label: "is" }],
};

const popoverClass = cn(
  "bg-popover text-popover-foreground rounded-md border shadow-md outline-none",
  "entering:animate-in entering:fade-in-0 entering:zoom-in-95",
  "exiting:animate-out exiting:fade-out-0 exiting:zoom-out-95",
);

/** Actual-style operator pill button */
function OpPill({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "h-6 px-2 rounded text-xs bg-accent hover:bg-accent/70",
        selected && "bg-primary text-primary-foreground hover:bg-primary/90",
      )}
    >
      {label}
    </button>
  );
}

export function TransactionFilters({
  filters,
  onFiltersChange,
  accounts,
  categories,
  excludeFields,
}: TransactionFiltersProps) {
  const isExcluded = (field: FilterField) => excludeFields?.includes(field) ?? false;
  const [activeFilters, setActiveFilters] = useState<Filter[]>([]);
  const [editingFilter, setEditingFilter] = useState<{
    field: FilterField;
    index?: number;
  } | null>(null);
  const [selectedOperator, setSelectedOperator] = useState<Operator | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const addFilterButtonRef = useRef<HTMLButtonElement>(null);

  // Initialize activeFilters from URL parameters
  useEffect(() => {
    const initialFilters: Filter[] = [];
    const currentMonthStart = startOfMonthFormatted();
    const currentMonthEnd = endOfMonthFormatted();

    // Date filters (only if different from defaults)
    if (filters.startDate && filters.startDate !== currentMonthStart) {
      initialFilters.push({
        field: "date",
        op: "gte",
        value: filters.startDate,
      });
    }
    if (filters.endDate && filters.endDate !== currentMonthEnd) {
      initialFilters.push({
        field: "date",
        op: "lte",
        value: filters.endDate,
      });
    }

    // Category filter
    if (filters.categoryId) {
      initialFilters.push({
        field: "category",
        op: "is",
        value: filters.categoryId,
      });
    }

    // Account filter (unless the page scopes to a single account already)
    if (filters.accountId && !isExcluded("account")) {
      initialFilters.push({
        field: "account",
        op: "is",
        value: filters.accountId,
      });
    }

    // Amount filters
    if (filters.minAmount !== undefined) {
      initialFilters.push({
        field: "amount",
        op: "gte",
        value: filters.minAmount,
      });
    }
    if (filters.maxAmount !== undefined) {
      initialFilters.push({
        field: "amount",
        op: "lte",
        value: filters.maxAmount,
      });
    }

    // Reviewed filter
    if (filters.isReviewed !== undefined) {
      initialFilters.push({
        field: "reviewed",
        op: "is",
        value: filters.isReviewed,
      });
    }

    // Note: type is not displayed as chips in this component
    // It is handled separately in the UI

    setActiveFilters(initialFilters);
  }, [
    filters.startDate,
    filters.endDate,
    filters.categoryId,
    filters.accountId,
    filters.minAmount,
    filters.maxAmount,
    // filters.type,
    filters.isReviewed,
  ]); // Re-run when filters change

  const handleAddFilter = (field: FilterField) => {
    setEditingFilter({ field });
    // Default to the first operator, like Actual's filter editor
    setSelectedOperator(OPERATORS[field][0].op);
    setSearchTerm("");
  };

  // biome-ignore lint/suspicious/noExplicitAny: filter value can be any
  const handleSetValue = (value: any) => {
    if (!editingFilter || !selectedOperator) return;

    const newFilter: Filter = {
      field: editingFilter.field,
      op: selectedOperator,
      value,
    };

    let updatedFilters: Filter[];
    if (editingFilter.index !== undefined) {
      // Editing existing filter
      updatedFilters = [...activeFilters];
      updatedFilters[editingFilter.index] = newFilter;
    } else {
      // Adding new filter
      updatedFilters = [...activeFilters, newFilter];
    }

    setActiveFilters(updatedFilters);
    applyFiltersToQuery(updatedFilters);
    setEditingFilter(null);
    setSelectedOperator(null);
    setSearchTerm("");
  };

  const handleRemoveFilter = (index: number) => {
    const updatedFilters = activeFilters.filter((_, i) => i !== index);
    setActiveFilters(updatedFilters);
    applyFiltersToQuery(updatedFilters);
  };

  const handleEditFilter = (index: number) => {
    const filter = activeFilters[index];
    setEditingFilter({ field: filter.field, index });
    setSelectedOperator(filter.op);
    setSearchTerm("");
  };

  const closeEditor = () => {
    setEditingFilter(null);
    setSelectedOperator(null);
    setSearchTerm("");
  };

  const applyFiltersToQuery = (filtersToApply: Filter[]) => {
    const updated = { ...filters };

    // Reset all filter fields (except excluded ones — those are page-scoped
    // and must survive filter edits, e.g. accountId on the account page)
    updated.categoryId = undefined;
    if (!isExcluded("account")) {
      updated.accountId = undefined;
    }
    updated.minAmount = undefined;
    updated.maxAmount = undefined;
    updated.type = undefined;
    updated.isReviewed = undefined;

    // Check if there are any date filters in filtersToApply
    const hasDateFilters = filtersToApply.some((f) => f.field === "date");

    // If there are date filters, reset to defaults first, then apply them
    // If there are no date filters, preserve the existing dates from props (URL params)
    if (hasDateFilters) {
      updated.startDate = startOfMonthFormatted();
      updated.endDate = endOfMonthFormatted();
    }

    // Apply each filter
    for (const filter of filtersToApply) {
      switch (filter.field) {
        case "date":
          if (filter.op === "is") {
            updated.startDate = filter.value;
            updated.endDate = filter.value;
          } else if (filter.op === "gt") {
            updated.startDate = dayjs(filter.value).add(1, "day").format("YYYY-MM-DD");
          } else if (filter.op === "gte") {
            updated.startDate = filter.value;
          } else if (filter.op === "lt") {
            updated.endDate = dayjs(filter.value).subtract(1, "day").format("YYYY-MM-DD");
          } else if (filter.op === "lte") {
            updated.endDate = filter.value;
          }
          break;
        case "category":
          if (filter.op === "is") {
            updated.categoryId = filter.value;
          }
          // isNot not supported by backend yet
          break;
        case "account":
          if (filter.op === "is") {
            updated.accountId = filter.value;
          }
          // isNot not supported by backend yet
          break;
        case "amount":
          if (filter.op === "is") {
            updated.minAmount = filter.value;
            updated.maxAmount = filter.value;
          } else if (filter.op === "gt") {
            updated.minAmount = filter.value + 0.01;
          } else if (filter.op === "gte") {
            updated.minAmount = filter.value;
          } else if (filter.op === "lt") {
            updated.maxAmount = filter.value - 0.01;
          } else if (filter.op === "lte") {
            updated.maxAmount = filter.value;
          }
          break;
        case "reviewed":
          if (filter.op === "is") {
            updated.isReviewed = filter.value;
          }
          break;
      }
    }

    onFiltersChange(updated);
  };

  const getFilterLabel = (filter: Filter): string => {
    switch (filter.field) {
      case "date":
        return dayjs(filter.value).format("DD/MM/YYYY");
      case "category":
        return categories.find((c) => c.id === filter.value)?.name || "";
      case "account":
        return accounts.find((a) => a.id === filter.value)?.accountName || "";
      case "amount":
        return `₹${filter.value}`;
      case "notes":
        return filter.value;
      case "reviewed":
        return filter.value ? "Reviewed" : "Not reviewed";
      default:
        return "";
    }
  };

  const getOperatorLabel = (field: FilterField, op: Operator): string => {
    return OPERATORS[field].find((o) => o.op === op)?.label || op;
  };

  const availableFields: FilterField[] = (
    ["date", "category", "account", "amount", "notes", "reviewed"] as FilterField[]
  ).filter((field) => !isExcluded(field));

  // Filtered categories based on search
  const filteredCategories = useMemo(() => {
    if (!searchTerm) return categories;
    return categories.filter((cat) => cat.name.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [categories, searchTerm]);

  // Filtered accounts based on search
  const filteredAccounts = useMemo(() => {
    if (!searchTerm) return accounts;
    return accounts.filter((acc) =>
      acc.accountName.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [accounts, searchTerm]);

  /** Actual-style single-panel filter editor: field title, op pills, value input, Apply */
  const renderFilterEditor = (field: FilterField) => {
    const editingValue =
      editingFilter?.index !== undefined ? activeFilters[editingFilter.index]?.value : undefined;

    // Boolean fields render value pills that apply immediately (like Actual)
    if (field === "reviewed") {
      return (
        <div className="p-3 w-56 space-y-3">
          <div className="text-sm font-medium">{FIELD_LABELS[field]}</div>
          <div className="flex flex-wrap gap-1">
            <OpPill label="Reviewed" selected={false} onClick={() => handleSetValue(true)} />
            <OpPill label="Not reviewed" selected={false} onClick={() => handleSetValue(false)} />
          </div>
        </div>
      );
    }

    return (
      <div className="p-3 w-64 space-y-3">
        {/* Field title */}
        <div className="text-sm font-medium">{FIELD_LABELS[field]}</div>

        {/* Operator pills */}
        <div className="flex flex-wrap gap-1">
          {OPERATORS[field].map((op) => (
            <OpPill
              key={op.op}
              label={op.label}
              selected={selectedOperator === op.op}
              onClick={() => setSelectedOperator(op.op)}
            />
          ))}
        </div>

        {/* Value input */}
        {renderValueInput(field, editingValue)}
      </div>
    );
  };

  const renderValueInput = (field: FilterField, editingValue?: unknown) => {
    switch (field) {
      case "date":
        return (
          <div className="space-y-2">
            <Input
              type="date"
              defaultValue={typeof editingValue === "string" ? editingValue : ""}
              className="h-8"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  const value = (e.target as HTMLInputElement).value;
                  if (value) handleSetValue(value);
                }
              }}
            />
            <div className="flex justify-end gap-1">
              <Button size="sm" variant="ghost" className="h-7" onClick={closeEditor}>
                Cancel
              </Button>
              <Button
                size="sm"
                className="h-7"
                onClick={(e) => {
                  const input = e.currentTarget
                    .closest(".space-y-2")
                    ?.querySelector("input") as HTMLInputElement | null;
                  if (input?.value) handleSetValue(input.value);
                }}
              >
                Apply
              </Button>
            </div>
          </div>
        );

      case "category":
        return (
          <div>
            <Input
              placeholder="Search categories..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="h-8 text-xs mb-1"
              autoFocus
            />
            <Menu
              className="outline-none max-h-64 overflow-y-auto -mx-1 px-1"
              onAction={(key) => handleSetValue(Number(key))}
            >
              {filteredCategories.length === 0 ? (
                <div className="px-3 py-2 text-sm text-muted-foreground">No categories found</div>
              ) : (
                filteredCategories.map((cat) => (
                  <MenuItem
                    key={cat.id}
                    id={cat.id.toString()}
                    className="px-3 py-1.5 text-sm cursor-pointer rounded hover:bg-accent focus:bg-accent outline-none"
                  >
                    {cat.name}
                  </MenuItem>
                ))
              )}
            </Menu>
          </div>
        );

      case "account":
        return (
          <div>
            <Input
              placeholder="Search accounts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="h-8 text-xs mb-1"
              autoFocus
            />
            <Menu
              className="outline-none max-h-64 overflow-y-auto -mx-1 px-1"
              onAction={(key) => handleSetValue(Number(key))}
            >
              {filteredAccounts.length === 0 ? (
                <div className="px-3 py-2 text-sm text-muted-foreground">No accounts found</div>
              ) : (
                filteredAccounts.map((acc) => (
                  <MenuItem
                    key={acc.id}
                    id={acc.id.toString()}
                    className="px-3 py-1.5 text-sm cursor-pointer rounded hover:bg-accent focus:bg-accent outline-none"
                  >
                    {acc.accountName}
                  </MenuItem>
                ))
              )}
            </Menu>
          </div>
        );

      case "amount":
        return (
          <div className="space-y-2">
            <Input
              type="number"
              placeholder="Amount"
              defaultValue={typeof editingValue === "number" ? editingValue : ""}
              className="h-8"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  const value = (e.target as HTMLInputElement).value;
                  if (value !== "") handleSetValue(Number(value));
                }
              }}
            />
            <div className="flex justify-end gap-1">
              <Button size="sm" variant="ghost" className="h-7" onClick={closeEditor}>
                Cancel
              </Button>
              <Button
                size="sm"
                className="h-7"
                onClick={(e) => {
                  const input = e.currentTarget
                    .closest(".space-y-2")
                    ?.querySelector("input") as HTMLInputElement | null;
                  if (input && input.value !== "") handleSetValue(Number(input.value));
                }}
              >
                Apply
              </Button>
            </div>
          </div>
        );

      case "notes":
        return (
          <div className="space-y-2">
            <Input
              type="text"
              placeholder="Notes text"
              defaultValue={typeof editingValue === "string" ? editingValue : ""}
              className="h-8"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  const value = (e.target as HTMLInputElement).value;
                  handleSetValue(value);
                }
              }}
            />
            <div className="flex justify-end gap-1">
              <Button size="sm" variant="ghost" className="h-7" onClick={closeEditor}>
                Cancel
              </Button>
              <Button
                size="sm"
                className="h-7"
                onClick={(e) => {
                  const input = e.currentTarget
                    .closest(".space-y-2")
                    ?.querySelector("input") as HTMLInputElement | null;
                  handleSetValue(input?.value ?? "");
                }}
              >
                Apply
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const renderFilterChip = (filter: Filter, index: number) => {
    return (
      <DialogTrigger key={index}>
        <AriaButton
          className={cn(
            "inline-flex items-center gap-1 h-7 pl-2.5 pr-1.5 rounded",
            "bg-accent hover:bg-accent/80",
            "text-xs",
            "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
          )}
          onPress={() => handleEditFilter(index)}
        >
          <span className="text-success font-medium">{FIELD_LABELS[filter.field]}</span>
          <span className="text-muted-foreground">{getOperatorLabel(filter.field, filter.op)}</span>
          <span>{getFilterLabel(filter)}</span>
          <X
            className="size-3 ml-1 text-muted-foreground hover:text-destructive cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleRemoveFilter(index);
            }}
            onPointerDown={(e) => {
              e.stopPropagation();
            }}
          />
        </AriaButton>
        <AriaPopover placement="bottom start" offset={4} className={popoverClass}>
          <Dialog className="outline-none">{renderFilterEditor(filter.field)}</Dialog>
        </AriaPopover>
      </DialogTrigger>
    );
  };

  return (
    <div className="flex items-center gap-1.5 flex-wrap w-full sm:w-auto">
      {/* Filter button (Actual-style funnel) */}
      <MenuTrigger>
        <AriaButton
          ref={addFilterButtonRef}
          className={cn(
            "inline-flex items-center gap-1.5 h-7 px-2 rounded-md border border-input bg-background",
            "text-xs font-medium hover:bg-accent hover:text-accent-foreground",
            "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
          )}
        >
          <ListFilter className="size-3.5" />
          Filter
          {activeFilters.length > 0 && (
            <span className="inline-flex items-center justify-center h-4 min-w-4 px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-medium">
              {activeFilters.length}
            </span>
          )}
        </AriaButton>
        <AriaPopover placement="bottom start" offset={4} className={cn(popoverClass, "min-w-32")}>
          <Menu
            className="p-1 outline-none"
            onAction={(key) => handleAddFilter(key as FilterField)}
          >
            {availableFields.map((field) => (
              <MenuItem
                key={field}
                id={field}
                className="px-2 py-1.5 text-sm cursor-pointer rounded hover:bg-accent focus:bg-accent outline-none"
              >
                {FIELD_LABELS[field]}
              </MenuItem>
            ))}
          </Menu>
        </AriaPopover>
      </MenuTrigger>

      {/* Applied filter pills */}
      {activeFilters.map((filter, index) => renderFilterChip(filter, index))}

      {/* New Filter Configuration Popover */}
      {editingFilter && editingFilter.index === undefined && addFilterButtonRef.current && (
        <AriaPopover
          triggerRef={addFilterButtonRef}
          placement="bottom start"
          offset={4}
          isOpen={true}
          onOpenChange={(isOpen) => {
            if (!isOpen) {
              closeEditor();
            }
          }}
          className={popoverClass}
        >
          {renderFilterEditor(editingFilter.field)}
        </AriaPopover>
      )}
    </div>
  );
}
